import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { PlusOutlined } from "@ant-design/icons";
import {
  App,
  Button,
  Flex,
  Input,
  Select,
} from "antd";
import type { AnyObject } from "antd/es/_util/type";
import { ColumnsType } from "antd/es/table";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import errorCodes from "@/apis/error";
import {
  disable as disableEquipment,
  enable as enableEquipment,
  list as listEquipment,
} from "@/apis/equipment";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";
import { CustomTabPaneContext } from "@/components/TabPane";
import useDataList from "@/hooks/useDataList";
import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef,
} from "@/styles/STableLayout";
import { RootState } from "@/store";
import { CommonStateEnum } from "@/types/common";
import {
  Equipment,
  EquipmentCategory,
  EquipmentRecordTypeEnum,
} from "@/types/equipment";
import { User, UserPermissionEnum } from "@/types/user";
import { hasPermission } from "@/utils/user";
import EquipmentDetail from "../EquipmentDetail";
import EquipmentForm from "../EquipmentForm";
import EquipmentPlaceLocationForm from "../EquipmentPlaceLocationForm";
import EquipmentQrCode from "../EquipmentQrCode";
import EquipmentRecordForm from "../EquipmentRecordForm";
import { useEquipmentEnumsContext } from "../../context/EquipmentEnumsContext";
import useEquipmentColumn from "../../hooks/useEquipmentColumn";
import { EquipmentListPanel } from "../../style";

/** 设备列表组件入参。 */
interface EquipmentListProps {
  category: EquipmentCategory;
}

/** 设备列表的查询条件。 */
interface EquipmentFilters {
  keyword: string;
  status?: number;
}

/** 当前打开的设备记录表单及其固定操作类型。 */
interface EquipmentRecordFormState {
  equipmentId: string;
  presetRecordType?: EquipmentRecordTypeEnum;
}

/** 设备列表的默认查询条件。 */
const INITIAL_EQUIPMENT_FILTERS: EquipmentFilters = {
  keyword: "",
  status: undefined,
};

/** 展示指定类目下的设备，并提供设备相关操作。 */
const EquipmentList = ({ category }: EquipmentListProps) => {
  const { t } = useTranslation();
  const { onOpen } = useContext(CustomTabPaneContext);
  const { modal, notification } = App.useApp();
  const currentUser: User = useSelector(
    (state: RootState) => state.userReducer.curUser,
  );
  const canEditEquipment = hasPermission(currentUser, [
    UserPermissionEnum.EQUIPMENT_ADD_OR_EDIT,
  ]);
  // 保存表格分页状态，并用于刷新当前页。
  const tableRef = useRef<TableLayoutRef | null>(null);
  // 记录当前列表请求，类目或筛选条件变化时取消旧请求。
  const listControllerRef = useRef<AbortController>();
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<EquipmentFilters>(INITIAL_EQUIPMENT_FILTERS);
  const { keyword, status } = draftFilters;
  const [items, total, loading, { fetchList, clearData, setList: setItems }] = useDataList<
    Equipment,
    Parameters<typeof listEquipment>
  >({ fetchApi: listEquipment });
  const [formOpen, setFormOpen] = useState(false);
  const [editingEquipmentId, setEditingEquipmentId] = useState<string>();
  const [editingEquipmentCategoryId, setEditingEquipmentCategoryId] =
    useState<string>();
  const [detailEquipmentId, setDetailEquipmentId] = useState<string>();
  const [qrEquipmentId, setQrEquipmentId] = useState<string>();
  const [recordFormState, setRecordFormState] =
    useState<EquipmentRecordFormState>();
  const [placeLocationEquipment, setPlaceLocationEquipment] =
    useState<Equipment>();
  // 标记正在切换启停状态的设备，避免重复操作。
  const [actionLoadingId, setActionLoadingId] = useState<string>();
  const { statusItems } = useEquipmentEnumsContext();
  // 禁用类目仅允许查看设备，不开放新增及设备操作。
  const categoryEnabled = category.state === CommonStateEnum.ACTIVE;

  // 加载当前类目设备，并阻止已取消请求覆盖最新结果。
  const loadList = useCallback(async (
    offset: number,
    filters: EquipmentFilters,
  ) => {
    listControllerRef.current?.abort();
    const controller = new AbortController();
    listControllerRef.current = controller;

    try {
      await fetchList({
        body: {
          categoryId: category.id,
          keyword: filters.keyword.trim() || null,
          status: filters.status ?? null,
        },
        pagination: {
          offset,
          take: tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE,
        },
        controller,
      });
    } catch (error) {
      if (!controller.signal.aborted) {
        notification.error({
          message: t("equipment.equipmentListFetchFailed"),
          description: errorCodes[Number(error)] ?? String(error),
        });
      }
    }
  }, [category.id, fetchList, notification, t]);

  // 切换类目时重置筛选、分页及已打开的操作弹窗。
  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_EQUIPMENT_FILTERS);
    clearData();
    setFormOpen(false);
    setEditingEquipmentId(undefined);
    setEditingEquipmentCategoryId(undefined);
    setDetailEquipmentId(undefined);
    setQrEquipmentId(undefined);
    setRecordFormState(undefined);
    setPlaceLocationEquipment(undefined);
    setActionLoadingId(undefined);
    tableRef.current?.resetTablePage();
    loadList(0, nextFilters);

    return () => listControllerRef.current?.abort();
  }, [clearData, loadList, replaceFilters]);

  const handleDetail = useCallback((id: string) => {
    setDetailEquipmentId(id);
  }, []);

  const handleEdit = useCallback((equipment: Equipment) => {
    setEditingEquipmentId(equipment.id);
    setEditingEquipmentCategoryId(equipment.categoryId);
    setFormOpen(true);
  }, []);

  const handleToggleEnabled = useCallback((equipment: Equipment) => {
    const enabled = equipment.state === CommonStateEnum.ACTIVE;

    // 用户确认后切换设备状态，并刷新当前页。
    modal.confirm({
      title: enabled
        ? t("equipment.disableEquipment")
        : t("equipment.enableEquipment"),
      content: enabled
        ? t("equipment.confirmDisableEquipment")
        : t("equipment.confirmEnableEquipment"),
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
      onOk: async () => {
        setActionLoadingId(equipment.id);
        try {
          if (enabled) {
            await disableEquipment(equipment.id);
          } else {
            await enableEquipment(equipment.id);
          }
          notification.success({
            message: t("equipment.equipmentStateUpdated"),
          });
          await loadList(tableRef.current?.offset ?? 0, appliedFilters);
        } catch (error) {
          notification.error({
            message: t("equipment.equipmentStateUpdateFailed"),
            description: errorCodes[Number(error)] ?? String(error),
          });
        } finally {
          setActionLoadingId(undefined);
        }
      },
    });
  }, [appliedFilters, loadList, modal, notification, t]);

  const handleQrCode = useCallback((id: string) => {
    setQrEquipmentId(id);
  }, []);

  const handleAddRecord = useCallback((
    id: string,
    presetRecordType?: EquipmentRecordTypeEnum,
  ) => {
    setRecordFormState({ equipmentId: id, presetRecordType });
  }, []);

  const handleViewRecords = useCallback((equipment: Equipment) => {
    onOpen("records", equipment);
  }, [onOpen]);

  const handleEditPlaceLocation = useCallback((equipment: Equipment) => {
    setPlaceLocationEquipment(equipment);
  }, []);

  const columns = useEquipmentColumn({
    statusItems,
    onDetail: handleDetail,
    onEdit: handleEdit,
    onToggleEnabled: handleToggleEnabled,
    onQrCode: handleQrCode,
    onAddRecord: handleAddRecord,
    onViewRecords: handleViewRecords,
    onEditPlaceLocation: handleEditPlaceLocation,
    actionLoadingId,
    actionsDisabled: !categoryEnabled,
    canEdit: canEditEquipment,
  });

  // 保存后，新增设备回到首页，编辑设备则停留在当前页。
  const handleSaved = () => {
    const wasEditing = Boolean(editingEquipmentId);
    setFormOpen(false);
    setEditingEquipmentId(undefined);
    setEditingEquipmentCategoryId(undefined);

    if (wasEditing) {
      loadList(tableRef.current?.offset ?? 0, appliedFilters);
    } else {
      tableRef.current?.resetTablePage();
      loadList(0, appliedFilters);
    }
  };

  const handleSearch = () => {
    const nextFilters = commitFilters();
    tableRef.current?.resetTablePage();
    loadList(0, nextFilters);
  };

  const handleResetFilters = () => {
    const nextFilters = replaceFilters(INITIAL_EQUIPMENT_FILTERS);
    tableRef.current?.resetTablePage();
    loadList(0, nextFilters);
  };

  const headerChildren = (
    <Flex
      justify="space-between"
      align="center"
      gap={12}
      style={{ width: "100%" }}
    >
      {canEditEquipment && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={!categoryEnabled}
          onClick={() => {
            setEditingEquipmentId(undefined);
            setEditingEquipmentCategoryId(undefined);
            setFormOpen(true);
          }}
        >
          {t("equipment.addEquipment")}
        </Button>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleResetFilters}
          disabled={loading}
        >
          {({ submit }) => (
            <>
             <Select
                value={status}
                allowClear
                placeholder={t("equipment.equipmentStatus")}
                options={statusItems.map(({ v, desc }) => ({
                  value: v,
                  label: desc,
                }))}
                style={{ width: 180 }}
                onChange={value => updateDraftFilters({ status: value })}
              />
              <Input
                value={keyword}
                allowClear
                placeholder={t("equipment.equipmentKeywordPlaceholder")}
                style={{ width: 200 }}
                onChange={event => updateDraftFilters({
                  keyword: event.target.value,
                })}
                onPressEnter={submit}
              />
            </>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  return (
    <EquipmentListPanel>
      <STableLayout
        rowKey="id"
        loading={loading}
        dataSource={items}
        columns={columns as ColumnsType<AnyObject>}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        scrollX={1760}
        handleChange={offset => loadList(offset, appliedFilters)}
      />
      {canEditEquipment && formOpen && (
        <EquipmentForm
          open={formOpen}
          category={category}
          equipmentId={editingEquipmentId}
          equipmentCategoryId={editingEquipmentCategoryId}
          onCancel={() => {
            setFormOpen(false);
            setEditingEquipmentId(undefined);
            setEditingEquipmentCategoryId(undefined);
          }}
          onSaved={handleSaved}
        />
      )}
      {detailEquipmentId && (
        <EquipmentDetail
          open={Boolean(detailEquipmentId)}
          equipmentId={detailEquipmentId}
          onCancel={() => setDetailEquipmentId(undefined)}
        />
      )}
      {qrEquipmentId && (
        <EquipmentQrCode
          open={Boolean(qrEquipmentId)}
          equipmentId={qrEquipmentId}
          onCancel={() => setQrEquipmentId(undefined)}
        />
      )}
      {canEditEquipment && recordFormState && (
        <EquipmentRecordForm
          open
          equipmentId={recordFormState.equipmentId}
          presetRecordType={recordFormState.presetRecordType}
          onCancel={() => setRecordFormState(undefined)}
          onSaved={() => {
            setRecordFormState(undefined);
            loadList(tableRef.current?.offset ?? 0, appliedFilters);
          }}
        />
      )}
      {canEditEquipment && placeLocationEquipment && (
        <EquipmentPlaceLocationForm
          open={Boolean(placeLocationEquipment)}
          equipment={placeLocationEquipment}
          onCancel={() => setPlaceLocationEquipment(undefined)}
          onSaved={(updatedEquipment) => {
            // 使用接口返回值局部更新当前行，无需重新请求列表。
            setItems(currentItems => currentItems.map(item => (
              item.id === updatedEquipment.id
                ? { ...item, ...updatedEquipment }
                : item
            )));
            setPlaceLocationEquipment(undefined);
          }}
        />
      )}
    </EquipmentListPanel>
  );
};

export default EquipmentList;
